/**
 * AdminProvidersList Component
 *
 * Admin version of providers list with optional organization filter
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Business as OrgIcon, PeopleOutline as EmptyIcon } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { useAdminProvidersList } from '../../hooks/admin';
import { ProviderRow } from '../providers/ProviderRow';

interface AdminProvidersListProps {
  /** Filter by organization ID */
  organizationId?: string;
  /** Organization name for display */
  organizationName?: string | null;
  /** Callback to clear organization filter */
  onClearFilter?: () => void;
}

export const AdminProvidersList: React.FC<AdminProvidersListProps> = ({
  organizationId,
  organizationName,
  onClearFilter,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { providers, total, isLoading, error, refetch, isEmpty } = useAdminProvidersList({
    organizationId,
  });

  const handleProviderClick = useCallback(
    (providerId: string) => {
      // Navigate to admin-v2 provider detail page
      navigate(`/admin-v2/provider/${providerId}`);
    },
    [navigate],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading providers</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Providers
          </Typography>
          {!isLoading && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {total} {total === 1 ? 'provider' : 'providers'}
              {organizationId ? ' in selected organization' : ' across all organizations'}
            </Typography>
          )}
        </Box>
        {organizationId && (
          <Chip
            icon={<OrgIcon sx={{ fontSize: 16 }} />}
            label={organizationName || organizationId.slice(0, 8)}
            onDelete={onClearFilter}
            color="primary"
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* List Container */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Loading State */}
        {isLoading && providers.length === 0 ? (
          <Box>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Skeleton variant="circular" width={44} height={44} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="40%" height={20} sx={{ mb: 0.5 }} />
                  <Skeleton width="20%" height={16} />
                </Box>
                <Skeleton width={60} height={20} />
                <Skeleton width={60} height={20} />
                <Skeleton width={80} height={24} />
              </Box>
            ))}
          </Box>
        ) : isEmpty ? (
          /* Empty State */
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: theme.palette.grey[100],
                mb: 2,
              }}
            >
              <EmptyIcon sx={{ fontSize: 32, color: theme.palette.grey[400] }} />
            </Box>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
              No providers found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
            >
              Audit data will appear here once notes are audited
            </Typography>
          </Box>
        ) : (
          /* Provider List */
          <Box sx={{ position: 'relative' }}>
            {isLoading && providers.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: alpha(theme.palette.background.paper, 0.7),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {providers.map(provider => (
              <ProviderRow
                key={provider.user_id}
                provider={provider}
                onClick={handleProviderClick}
              />
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AdminProvidersList;

